/**
 * Fechas de la agenda, en la hora de Cartago.
 *
 * Todo se reduce a la cadena aaaa-mm-dd del día en Bogotá y se opera sobre
 * ella. Restar objetos Date con husos horarios de por medio es la forma más
 * fácil de que una orden programada a las 6 de la tarde aparezca al día
 * siguiente.
 */

const ZONA = 'America/Bogota'

const FORMATO_DIA = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const FORMATO_HORA = new Intl.DateTimeFormat('es-CO', {
  timeZone: ZONA,
  hour: 'numeric',
  minute: '2-digit',
})

const FORMATO_MES = new Intl.DateTimeFormat('es-CO', { timeZone: 'UTC', month: 'long' })

/** El día en Cartago de una marca de tiempo, como aaaa-mm-dd. */
export function dia(iso: string): string {
  return FORMATO_DIA.format(new Date(iso))
}

/** La hora en Cartago: «7:30 a. m.». */
export function hora(iso: string): string {
  return FORMATO_HORA.format(new Date(iso))
}

/** Hoy en Cartago. */
export function hoy(): string {
  return FORMATO_DIA.format(new Date())
}

/** Días desde la época: permite restar y comparar sin husos de por medio. */
export function indice(dia: string): number {
  return Date.parse(`${dia}T00:00:00Z`) / 86_400_000
}

export function sumarDias(dia: string, cuantos: number): string {
  return new Date((indice(dia) + cuantos) * 86_400_000).toISOString().slice(0, 10)
}

/** El lunes de la semana a la que pertenece un día. */
export function lunesDe(dia: string): string {
  const diaSemana = new Date(`${dia}T00:00:00Z`).getUTCDay()
  // getUTCDay da 0 el domingo; en Colombia la semana arranca el lunes.
  return sumarDias(dia, diaSemana === 0 ? -6 : 1 - diaSemana)
}

/** Los siete días de una semana, a partir de su lunes. */
export function semanaDesde(lunes: string): string[] {
  return Array.from({ length: 7 }, (_, i) => sumarDias(lunes, i))
}

export const NOMBRE_DIA = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom']

/** Cuál de NOMBRE_DIA le toca, con el lunes en la posición cero. */
export function nombreDe(dia: string): string {
  const diaSemana = new Date(`${dia}T00:00:00Z`).getUTCDay()
  return NOMBRE_DIA[(diaSemana + 6) % 7]
}

export function numeroDe(dia: string): number {
  return Number(dia.slice(-2))
}

export function esFinDeSemana(dia: string): boolean {
  const diaSemana = new Date(`${dia}T00:00:00Z`).getUTCDay()
  return diaSemana === 0 || diaSemana === 6
}

/** «10 al 16 de agosto de 2026», o con los dos meses cuando la semana los cruza. */
export function rotuloSemana(dias: string[]): string {
  const primero = dias[0]
  const ultimo = dias[dias.length - 1]

  const mesPrimero = FORMATO_MES.format(new Date(`${primero}T00:00:00Z`))
  const mesUltimo = FORMATO_MES.format(new Date(`${ultimo}T00:00:00Z`))
  const anio = ultimo.slice(0, 4)

  if (mesPrimero === mesUltimo) {
    return `${numeroDe(primero)} al ${numeroDe(ultimo)} de ${mesUltimo} de ${anio}`
  }

  return `${numeroDe(primero)} de ${mesPrimero} al ${numeroDe(ultimo)} de ${mesUltimo} de ${anio}`
}
